import React, { useState, useEffect, useRef, useMemo } from "react";
import { downloadCsv } from "../common/sharedFunctions";
import MaterialTable from "material-table";
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { api } from "common";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import moment from "moment/min/moment-with-locales";
import { Select, MenuItem, Box, Badge, Tooltip, IconButton, TextField } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import ClearIcon from "@mui/icons-material/Clear";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import { ThemeProvider } from "@mui/material/styles";
import BlankTable from "../components/Table/BlankTable";
import localization from "../components/Table/Localization";
import VerifyIdDocumentModal from "../components/VerifyIdDocumentModal";
import {
  C2,
  C2_FONT,
  c2TableTheme,
  c2TableOptions,
  c2TableStyle,
  c2Toolbar,
  c2Localization,
  C2TableCard,
  C2SectionHeading,
  C2EmptyState,
  C2UserAvatar,
  C2DocsChip,
  C2Switch,
  C2Select,
  C2StatusChip,
  getDriverApprovalStatus,
  isReuploadedAfterReject,
  hasPendingDocReview,
  isUserDocVerified,
  getUserDocTimestamp,
  C2VerifiedBadge,
  C2DocsBadge,
} from "../components/Table/C2UserTable";

/** Conductores de los primeros 100 cupos por ciudad (signupMode city_special). */
export default function Lideres() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const {
    editUser,
    fetchUsersOnce,
    fetchZonificacionCities,
    fetchLeaderAlerts,
    editLeaderAlert,
    fetchCars,
    fetchCarTypes,
  } = api;
  const [data, setData] = useState([]);
  const [docsUser, setDocsUser] = useState(null);
  const staticusers = useSelector((state) => state.usersdata.staticusers);
  const zonificacionCities = useSelector((state) => state.zonificacion?.cities);
  const carlistdata = useSelector((state) => state.carlistdata?.cars);
  const cartypes = useSelector((state) => state.cartypes?.cars);
  const leaderAlerts = useSelector((state) => state.leaderAlerts?.alerts || []);
  const auth = useSelector((state) => state.auth);
  const settings = useSelector((state) => state.settingsdata.settings);
  const dispatch = useDispatch();
  const loaded = useRef(false);
  const [sortedData, setSortedData] = useState([]);
  const [cityFilter, setCityFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortFilter, setSortFilter] = useState("createdAt_desc");
  const [dateFilter, setDateFilter] = useState("");
  const [vehicleFilter, setVehicleFilter] = useState("");
  const [alertStatusFilter, setAlertStatusFilter] = useState("");
  const { state } = useLocation();
  const [currentPage, setCurrentPage] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedAlertRow, setSelectedAlertRow] = useState(null);

  const citiesById = useMemo(() => {
    const map = {};
    (zonificacionCities || []).forEach((c) => {
      if (c?.id) map[c.id] = c.name || c.id;
    });
    return map;
  }, [zonificacionCities]);

  const cityFilterOptions = useMemo(() => {
    const options = new Map();
    (zonificacionCities || []).forEach((c) => {
      if (c?.id) options.set(c.id, c.name || c.id);
    });
    (data || []).forEach((d) => {
      if (d.cityId && !options.has(d.cityId)) {
        options.set(d.cityId, d.cityName || d.cityId);
      }
    });
    return Array.from(options.entries())
      .map(([cid, name]) => ({ id: cid, name }))
      .sort((a, b) => a.name.localeCompare(b.name, "es"));
  }, [zonificacionCities, data]);

  useEffect(() => {
    setCurrentPage(state?.pageNo);
  }, [state]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  useEffect(() => {
    dispatch(fetchUsersOnce({ includeLocations: false }));
    dispatch(fetchZonificacionCities());
    if (fetchLeaderAlerts) dispatch(fetchLeaderAlerts());
    if (typeof fetchCars === "function") dispatch(fetchCars());
    if (typeof fetchCarTypes === "function") dispatch(fetchCarTypes());
  }, [dispatch, fetchUsersOnce, fetchZonificacionCities, fetchLeaderAlerts, fetchCars, fetchCarTypes]);

  const activeCarByDriver = useMemo(() => {
    const map = {};
    (carlistdata || []).forEach((c) => {
      if (c?.driver) {
        if (!map[c.driver] || c.active) {
          map[c.driver] = c;
        }
      }
    });
    return map;
  }, [carlistdata]);

  const vehicleFilterOptions = useMemo(() => {
    const set = new Set();
    (cartypes || []).forEach((ct) => {
      if (ct?.name) set.add(ct.name);
    });
    (data || []).forEach((d) => {
      if (d.carType) set.add(d.carType);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "es"));
  }, [cartypes, data]);

  useEffect(() => {
    if (staticusers) {
      setTimeout(() => {
        setData(
          staticusers
            .filter(
              (user) =>
                user.usertype === "driver" &&
                user.signupMode === "city_special" &&
                ((user.fleetadmin === auth.profile.uid &&
                  auth.profile.usertype === "fleetadmin") ||
                  auth.profile.usertype === "admin")
            )
            .map((user) => {
              const car = activeCarByDriver[user.id];
              const resolvedCarType = user.carType || car?.carType || user.vehicleType || "";
              return {
                ...user,
                cityName:
                  user.cityName ||
                  (user.cityId ? citiesById[user.cityId] : "") ||
                  "",
                carType: resolvedCarType,
                vehicleNumber: user.vehicleNumber || car?.vehicleNumber || "",
              };
            })
        );
      }, 1000);
    } else {
      setData([]);
    }
    loaded.current = true;
  }, [
    staticusers,
    auth.profile.usertype,
    auth.profile.uid,
    citiesById,
    activeCarByDriver,
  ]);

  useEffect(() => {
    if (data) {
      setSortedData(
        [...data].sort((a, b) => moment(b.createdAt) - moment(a.createdAt))
      );
    }
  }, [data]);

  const reuploadedCount = useMemo(() => {
    return (sortedData || []).filter(isReuploadedAfterReject).length;
  }, [sortedData]);

  const pendingDocsCount = useMemo(() => {
    return (sortedData || []).filter(hasPendingDocReview).length;
  }, [sortedData]);

  const filteredData = useMemo(() => {
    let list = (sortedData || []).filter((d) => {
      // 1. Filtro por Ciudad
      if (cityFilter) {
        if (cityFilter === "__none__") {
          if (d.cityId || d.cityName) return false;
        } else if (d.cityId !== cityFilter && d.cityName !== cityFilter) {
          return false;
        }
      }

      // 2. Filtro por Estado (Pendiente, Aprobado, Rechazado, Reenviaron documento, Por revisar documentos)
      if (statusFilter) {
        const isReup = isReuploadedAfterReject(d);
        if (statusFilter === "reuploaded") {
          if (!isReup) return false;
        } else if (statusFilter === "pending_docs") {
          if (!hasPendingDocReview(d)) return false;
        } else {
          const approval = getDriverApprovalStatus(d);
          if (statusFilter === "approved" && approval !== "approved") return false;
          if (statusFilter === "rejected" && approval !== "rejected") return false;
          if (statusFilter === "pending" && approval !== "pending") return false;
          if (statusFilter === "no_docs" && approval !== "no_docs") return false;
        }
      }

      // 3. Filtro por Día (Fecha exacta del registro)
      if (dateFilter) {
        const itemDate = d.createdAt ? moment(d.createdAt).format("YYYY-MM-DD") : "";
        if (itemDate !== dateFilter) return false;
      }

      // 4. Filtro por Tipo de Vehículo
      if (vehicleFilter) {
        if ((d.carType || "").toLowerCase() !== vehicleFilter.toLowerCase()) return false;
      }

      return true;
    });

    // 5. Ordenamiento (Registro recientes vs Últimos documentos subidos primero)
    if (sortFilter === "docs_desc") {
      return [...list].sort((a, b) => {
        const timeB = getUserDocTimestamp(b);
        const timeA = getUserDocTimestamp(a);
        if (timeB !== timeA) return timeB - timeA;
        return moment(b.createdAt) - moment(a.createdAt);
      });
    }

    return list;
  }, [sortedData, cityFilter, statusFilter, dateFilter, vehicleFilter, sortFilter]);

  const handelApproved = (rowData) => {
    const updatedUser = {
      ...rowData,
      approved: !rowData.approved,
      adminApprovedTrue: true,
    };
    dispatch(editUser(updatedUser.id, updatedUser));
    dispatch(fetchUsersOnce({ force: true, includeLocations: false }));
    return updatedUser;
  };

  const filteredAlerts = useMemo(() => {
    let list = leaderAlerts || [];
    if (cityFilter && cityFilter !== "__none__") {
      list = list.filter((a) => a.cityId === cityFilter);
    }
    if (alertStatusFilter) {
      list = list.filter((a) => a.status === alertStatusFilter);
    }
    return list;
  }, [leaderAlerts, cityFilter, alertStatusFilter]);

  const handleAlertStatusChange = (row, newStatus) => {
    if (!editLeaderAlert || !settings.AllowCriticalEditsAdmin) return;
    dispatch(
      editLeaderAlert(
        {
          ...row,
          status: newStatus,
        },
        "Update"
      )
    );
  };

  const alertColumns = [
    {
      title: "Fecha",
      field: "createdAt",
      defaultSort: "desc",
      render: (row) =>
        row.createdAt ? moment(row.createdAt).format("lll") : "—",
    },
    {
      title: "Semana",
      field: "weekKey",
      render: (row) => row.weekKey || "—",
    },
    {
      title: "Conductor",
      field: "leaderName",
      render: (row) => row.leaderName || "—",
    },
    {
      title: "Ciudad",
      field: "cityName",
      render: (row) => row.cityName || "—",
    },
    {
      title: "Avance semana",
      field: "progress",
      render: (row) =>
        `Cond. ${row.driversRegistered || 0}/${row.driversGoal || 2} · Usu. ${
          row.customersRegistered || 0
        }/${row.customersGoal || 2}`,
    },
    {
      title: "Incumplió",
      field: "missedLabel",
      render: (row) => row.missedLabel || "—",
    },
    {
      title: "Nivel 1",
      field: "level1",
      render: (row) =>
        `${row.level1SlotsUsed || 0}/${row.level1SlotsTotal || 50}`,
    },
    {
      title: "Estado",
      field: "status",
      render: (row) => (
        <Select
          size="small"
          value={row.status || "pendiente"}
          disabled={!settings.AllowCriticalEditsAdmin}
          onChange={(e) => handleAlertStatusChange(row, e.target.value)}
          sx={{
            minWidth: 130,
            borderRadius: "10px",
            fontFamily: C2_FONT,
            fontSize: "0.85rem",
            color: C2.dark,
            "& fieldset": { borderColor: C2.border },
            "&.Mui-focused fieldset": { borderColor: C2.violet },
          }}
        >
          <MenuItem value="pendiente" sx={{ fontFamily: C2_FONT }}>Pendiente</MenuItem>
          <MenuItem value="revisada" sx={{ fontFamily: C2_FONT }}>Revisada</MenuItem>
          <MenuItem value="gestionada" sx={{ fontFamily: C2_FONT }}>Gestionada</MenuItem>
        </Select>
      ),
    },
  ];

  const columns = [
    {
      title: t("createdAt"),
      field: "createdAt",
      editable: "never",
      defaultSort: "desc",
      render: (rowData) =>
        rowData.createdAt ? moment(rowData.createdAt).format("lll") : null,
    },
    {
      title: t("first_name"),
      field: "firstName",
      editable: "never",
      render: (rowData) => {
        const isReup = isReuploadedAfterReject(rowData);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
            <Box component="span" sx={{ fontFamily: C2_FONT, fontSize: "0.85rem", fontWeight: 600, color: C2.dark }}>
              {rowData.firstName || ""}
            </Box>
            <C2VerifiedBadge user={rowData} />
            {isReup ? (
              <Tooltip title="⚠️ Este líder fue rechazado anteriormente y volvió a subir documentos para revisión">
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    px: 0.6,
                    py: 0.15,
                    borderRadius: "6px",
                    bgcolor: "#FFF3E0",
                    color: "#E65100",
                    border: "1px solid #FFE0B2",
                    fontSize: "0.68rem",
                    fontWeight: 800,
                    fontFamily: C2_FONT,
                    whiteSpace: "nowrap",
                  }}
                >
                  🔔 Reenviado
                </Box>
              </Tooltip>
            ) : null}
          </Box>
        );
      },
    },
    { title: t("last_name"), field: "lastName", editable: "never" },
    {
      title: "Ciudad",
      field: "cityName",
      editable: "never",
      render: (rowData) => rowData.cityName || "—",
    },
    {
      title: "Tipo vehículo",
      field: "carType",
      editable: "never",
      render: (rowData) => {
        if (!rowData.carType) return "—";
        return (
          <C2StatusChip
            tone="neutral"
            label={rowData.carType}
            dot={false}
          />
        );
      },
    },
    {
      title: "Nivel red",
      field: "networkLevel",
      editable: "never",
      render: (rowData) => {
        const net = api.normalizeNetworkState
          ? api.normalizeNetworkState(rowData, rowData.id)
          : { inNetwork: false };
        if (net.inNetwork) return api.networkLevelLabel(net.level);
        return rowData.signupMode === "city_special"
          ? api.networkLevelLabel(0)
          : "—";
      },
    },
    {
      title: "Nivel 1 (cupos)",
      field: "directSlotsUsed",
      editable: "never",
      render: (rowData) => {
        const net = api.normalizeNetworkState
          ? api.normalizeNetworkState(rowData, rowData.id)
          : { inNetwork: false };
        if (net.inNetwork) return api.formatDirectSlots(net.used, net.total);
        if (rowData.signupMode === "city_special") {
          return api.formatDirectSlots(
            rowData.directSlotsUsed || 0,
            rowData.directSlotsTotal || api.DIRECT_SLOTS_PER_LEVEL || 50
          );
        }
        return "—";
      },
    },
    {
      title: "Semana conductores",
      field: "weeklyDrivers",
      editable: "never",
      render: (rowData) => {
        const w = api.formatWeeklyProgress
          ? api.formatWeeklyProgress(rowData.leaderWeekly)
          : null;
        if (!w) {
          const d = Number(rowData.leaderWeekly?.drivers) || 0;
          return `${d}/2`;
        }
        return (
          <span style={{ color: w.driversOk ? "#2e7d32" : "#c62828" }}>
            {w.driversLabel}
          </span>
        );
      },
    },
    {
      title: "Semana usuarios",
      field: "weeklyCustomers",
      editable: "never",
      render: (rowData) => {
        const w = api.formatWeeklyProgress
          ? api.formatWeeklyProgress(rowData.leaderWeekly)
          : null;
        if (!w) {
          const c = Number(rowData.leaderWeekly?.customers) || 0;
          return `${c}/2`;
        }
        return (
          <span style={{ color: w.customersOk ? "#2e7d32" : "#c62828" }}>
            {w.customersLabel}
          </span>
        );
      },
    },
    {
      title: "Meta semanal",
      field: "weeklyMeta",
      editable: "never",
      render: (rowData) => {
        const net = api.normalizeNetworkState
          ? api.normalizeNetworkState(rowData, rowData.id)
          : { inNetwork: false };
        const used = net.inNetwork
          ? net.used
          : Number(rowData.directSlotsUsed) || 0;
        const total = net.inNetwork
          ? net.total || 50
          : rowData.directSlotsTotal != null
            ? Number(rowData.directSlotsTotal)
            : 50;
        if (used >= total) return "Completó Nivel 1";
        const w = api.formatWeeklyProgress
          ? api.formatWeeklyProgress(rowData.leaderWeekly)
          : { driversOk: false, customersOk: false };
        if (w.driversOk && w.customersOk) return "En meta";
        return "Bajo meta";
      },
    },
    {
      title: "Tope mensual",
      field: "earningsCap",
      editable: "never",
      render: (rowData) => {
        const p = api.formatEarningsCapProgress
          ? api.formatEarningsCapProgress(rowData, rowData.id)
          : null;
        return p ? p.label : "—";
      },
    },
    {
      title: "Código ingreso",
      field: "signupEntryCode",
      editable: "never",
      render: (rowData) =>
        rowData.signupEntryCode || rowData.citySignupCode || "—",
    },
    {
      title: "Código cupo",
      field: "citySignupCode",
      editable: "never",
      render: (rowData) => rowData.citySignupCode || "—",
    },
    {
      title: "Estado estructura",
      field: "networkStructureStatus",
      editable: "never",
      render: (rowData) =>
        api.networkStructureStatusLabel
          ? api.networkStructureStatusLabel(rowData.networkStructureStatus)
          : rowData.networkStructureStatus || "—",
    },
    {
      title: t("mobile"),
      field: "mobile",
      editable: "never",
      render: (rowData) =>
        settings.AllowCriticalEditsAdmin ? rowData.mobile : t("hidden_demo"),
    },
    {
      title: t("email"),
      field: "email",
      editable: "never",
      render: (rowData) =>
        settings.AllowCriticalEditsAdmin ? rowData.email : t("hidden_demo"),
    },
    {
      title: t("profile_image"),
      field: "profile_image",
      render: (rowData) => (
        <C2UserAvatar src={rowData.profile_image} firstName={rowData.firstName} lastName={rowData.lastName} />
      ),
      editable: "never",
    },
    {
      title: t("account_approve"),
      field: "approved",
      type: "boolean",
      render: (rowData) => {
        const approval = getDriverApprovalStatus(rowData);
        const isReup = isReuploadedAfterReject(rowData);
        return (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
            <C2Switch
              disabled={!settings.AllowCriticalEditsAdmin}
              checked={rowData.approved}
              onChange={() => handelApproved(rowData)}
            />
            <C2StatusChip
              tone={
                approval === "approved"
                  ? "success"
                  : approval === "rejected"
                  ? "danger"
                  : approval === "no_docs"
                  ? "neutral"
                  : "warning"
              }
              label={
                approval === "approved"
                  ? "Aprobado"
                  : approval === "rejected"
                  ? "Rechazado"
                  : isReup
                  ? "Reenviado"
                  : approval === "no_docs"
                  ? "Sin documentos"
                  : "Pendiente"
              }
            />
          </Box>
        );
      },
    },
    {
      title: t("driver_active"),
      field: "driverActiveStatus",
      editable: "never",
      type: "boolean",
      render: (rowData) => (
        <C2Switch disabled checked={!!rowData.driverActiveStatus} />
      ),
    },
  ];

  const leadersComponents = c2Toolbar(
    pendingDocsCount > 0 ? (
      <Tooltip title="Filtrar líderes con documentos pendientes de revisión">
        <Box
          onClick={() => setStatusFilter(statusFilter === "pending_docs" ? "" : "pending_docs")}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.7,
            px: 1.4,
            py: 0.6,
            borderRadius: "10px",
            bgcolor: statusFilter === "pending_docs" ? "#E65100" : "#FFF3E0",
            color: statusFilter === "pending_docs" ? "#FFFFFF" : "#E65100",
            border: "1.5px solid #FF9800",
            cursor: "pointer",
            fontWeight: 800,
            fontSize: "0.8rem",
            fontFamily: C2_FONT,
            boxShadow: "0 2px 8px rgba(255,152,0,0.2)",
            transition: "all 0.2s ease",
            "&:hover": {
              transform: "translateY(-1px)",
              boxShadow: "0 4px 12px rgba(255,152,0,0.35)",
            },
          }}
        >
          <NotificationsActiveOutlinedIcon sx={{ fontSize: 17 }} />
          <span>{pendingDocsCount} {pendingDocsCount === 1 ? "doc por revisar" : "docs por revisar"}</span>
        </Box>
      </Tooltip>
    ) : null,
    <C2Select
      id="lider-sort-filter"
      label="Ordenar por"
      value={sortFilter}
      onChange={(e) => setSortFilter(e.target.value)}
      minWidth={230}
      options={[
        { value: "createdAt_desc", label: "Registro (recientes primero)" },
        { value: "docs_desc", label: "Últimos docs subidos primero" },
      ]}
    />,
    <C2Select
      id="lider-status-filter"
      label="Estado"
      value={statusFilter}
      onChange={(e) => setStatusFilter(e.target.value)}
      minWidth={170}
      options={[
        { value: "", label: "Todos los estados" },
        { value: "pending_docs", label: "Por revisar documentos" },
        { value: "pending", label: "Pendiente" },
        { value: "approved", label: "Aprobado" },
        { value: "rejected", label: "Rechazado" },
        { value: "reuploaded", label: "Reenviaron documento" },
        { value: "no_docs", label: "Sin documentos" },
      ]}
    />,
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        backgroundColor: "#FFFFFF",
        borderRadius: "10px",
        border: `1px solid ${C2.border}`,
        px: 1,
        py: 0.4,
        height: 40,
        boxSizing: "border-box",
      }}
    >
      <Box component="span" sx={{ fontFamily: C2_FONT, fontSize: "0.78rem", color: C2.muted, whiteSpace: "nowrap" }}>
        Día:
      </Box>
      <input
        type="date"
        value={dateFilter}
        onChange={(e) => setDateFilter(e.target.value)}
        style={{
          border: "none",
          outline: "none",
          fontFamily: C2_FONT,
          fontSize: "0.85rem",
          color: C2.dark,
          backgroundColor: "transparent",
          cursor: "pointer",
        }}
      />
      {dateFilter ? (
        <IconButton
          size="small"
          onClick={() => setDateFilter("")}
          sx={{ p: 0.2, color: C2.muted, "&:hover": { color: C2.dark } }}
          title="Limpiar fecha"
        >
          <ClearIcon sx={{ fontSize: 16 }} />
        </IconButton>
      ) : null}
    </Box>,
    <C2Select
      id="lider-vehicle-filter"
      label="Tipo vehículo"
      value={vehicleFilter}
      onChange={(e) => setVehicleFilter(e.target.value)}
      minWidth={170}
      options={[
        { value: "", label: "Todos los vehículos" },
        { value: "__none__", label: "Sin vehículo" },
        ...vehicleFilterOptions.map((v) => ({ value: v, label: v })),
      ]}
    />,
    <C2Select
      id="lider-city-filter"
      label="Filtrar por ciudad"
      value={cityFilter}
      onChange={(e) => setCityFilter(e.target.value)}
      options={[
        { value: "", label: "Todas" },
        { value: "__none__", label: "Sin ciudad" },
        ...cityFilterOptions.map((c) => ({ value: c.id, label: c.name })),
      ]}
    />
  );
  const leadersLocalization = c2Localization(
    localization(t),
    <C2EmptyState
      icon={HubOutlinedIcon}
      message={
        cityFilter
          ? "No hay líderes para este filtro. Prueba con otra ciudad."
          : "Aún no hay líderes. Los conductores que se registren con un cupo especial de ciudad aparecerán aquí."
      }
    />
  );
  const alertsComponents = c2Toolbar(
    <C2Select
      id="alert-status-filter"
      label="Estado alerta"
      value={alertStatusFilter}
      onChange={(e) => setAlertStatusFilter(e.target.value)}
      options={[
        { value: "", label: "Todas" },
        { value: "pendiente", label: "Pendiente" },
        { value: "revisada", label: "Revisada" },
        { value: "gestionada", label: "Gestionada" },
      ]}
    />
  );
  const alertsLocalization = c2Localization(
    localization(t),
    <C2EmptyState
      icon={NotificationsActiveOutlinedIcon}
      title="Sin alertas por ahora"
      message="Cuando un líder no cumpla su meta semanal, la alerta aparecerá aquí para que el equipo la revise."
    />
  );

  return !loaded.current ? (
    <CircularLoading />
  ) : (
    <div>
      <ThemeProvider theme={c2TableTheme}>
        <C2TableCard>
        {sortedData?.length > 0 ? (
          <MaterialTable
            title="Líderes"
            columns={columns}
            onChangePage={(page) => handlePageChange(page)}
            style={c2TableStyle(isRTL)}
            data={filteredData}
            components={leadersComponents}
            onRowClick={(evt, selectedRowData) =>
              setSelectedRow(selectedRowData.tableData.id)
            }
            options={{
              ...c2TableOptions(selectedRow),
              pageSize: 20,
              pageSizeOptions: [10, 20, 50, 100],
              initialPage: state?.pageNo,
              exportCsv: (cols, rows) => {
                let hArray = [];
                const headerRow = cols.map((col) => {
                  hArray.push(col.field);
                  return col.title;
                });
                const dataRows = rows.map(({ tableData, ...row }) => {
                  row.createdAt =
                    new Date(row.createdAt).toLocaleDateString() +
                    " " +
                    new Date(row.createdAt).toLocaleTimeString();
                  let dArr = [];
                  for (let i = 0; i < hArray.length; i++) {
                    dArr.push(row[hArray[i]]);
                  }
                  return Object.values(dArr);
                });
                const csvContent = [headerRow, ...dataRows]
                  .map((e) => e.join(","))
                  .join("\n");
                downloadCsv(csvContent, "lideres.csv");
              },
              exportButton: {
                csv: settings.AllowCriticalEditsAdmin,
                pdf: false,
              },
              maxColumnSort: "all_columns",
            }}
            localization={leadersLocalization}
            actions={[
              (rowData) => ({
                tooltip: t("edit"),
                icon: () => (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      marginRight: 0,
                    }}
                  >
                    <EditIcon />
                  </div>
                ),
                onClick: (event, row) => {
                  navigate(`/users/edituser/driver/${row.id}`, {
                    state: { pageNo: currentPage },
                  });
                },
              }),
              {
                icon: "info",
                tooltip: t("profile_page_subtitle"),
                onClick: (event, rowData) => {
                  navigate(`/users/driverdetails/${rowData.id}`, {
                    state: { pageNo: currentPage },
                  });
                },
              },
              (rowData) => {
                const isPending = hasPendingDocReview(rowData);
                const isReup = isReuploadedAfterReject(rowData);
                const isVerified = isUserDocVerified(rowData);
                return {
                  icon: () => (
                    <C2DocsBadge isPending={isPending} isReup={isReup}>
                      <C2DocsChip label={t("documents")} />
                    </C2DocsBadge>
                  ),
                  tooltip: isReup
                    ? "⚠️ Líder rechazado reenvió documentos para revisión"
                    : isPending
                    ? "🔔 Documentos pendientes de revisión y aprobación"
                    : isVerified
                    ? "✓ Documentos verificados"
                    : t("documents"),
                  onClick: () => setDocsUser(rowData),
                };
              },
            ]}
          />
        ) : (
          <BlankTable
            title="Líderes"
            columns={columns}
            actions={[]}
            data={[]}
            components={leadersComponents}
            style={c2TableStyle(isRTL)}
            localization={leadersLocalization}
            options={{ ...c2TableOptions(null), pageSize: 20, pageSizeOptions: [10, 20, 50, 100] }}
          />
        )}
        </C2TableCard>

        <C2TableCard sx={{ mt: 3 }}>
          <Box sx={{ px: { xs: 0.5, sm: 1 }, pt: { xs: 1, sm: 0.5 } }}>
            <C2SectionHeading
              title="Alertas de actividad semanal"
              subtitle="Incumplimientos de meta (≥2 conductores y ≥2 usuarios por semana). No se suspende automáticamente; el equipo decide."
            />
          </Box>
          <MaterialTable
            title="Alertas líderes"
            columns={alertColumns}
            data={filteredAlerts}
            components={alertsComponents}
            onRowClick={(evt, row) => setSelectedAlertRow(row.tableData.id)}
            style={c2TableStyle(isRTL)}
            options={{
              ...c2TableOptions(selectedAlertRow),
              pageSize: 20,
              pageSizeOptions: [10, 20, 50, 100],
              exportButton: {
                csv: settings.AllowCriticalEditsAdmin,
                pdf: false,
              },
            }}
            localization={alertsLocalization}
          />
        </C2TableCard>
      </ThemeProvider>
      <VerifyIdDocumentModal
        open={!!docsUser}
        user={docsUser}
        onClose={() => setDocsUser(null)}
      />
    </div>
  );
}
